import React, { useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, 
  Users, 
  FileText, 
  Check, 
  Lock,
  Database,
  Building,
  Globe,
  Settings,
  Loader2
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@/data/store/utils/redux';
import { getAllPermissionsAsync } from '@/data/store/actions';
import { getAllPermissions } from '@/data/store/selectors';
import { TPermissionItem, TRoleItem } from '@/data/types';
import { InfiniteScroll } from '@/components/common/InfiniteScroll/InfiniteScroll';
import { getCategoryInfo } from '@/utils/roles';
import PermissionGroupComponent, { PermissionGroup } from './PermissionGroup';

interface PermissionsPanelProps {
  selectedRole: TRoleItem | null;
  selectedPermissions: string[];
  onPermissionToggle: (permissionId: string) => void;
  isVisible: boolean;
  permissionsLocked?: boolean;
  isLoadingRoleDetails?: boolean;
  rolePermissions?: TPermissionItem[];
  isRoleEditing?: boolean;
}

const PermissionsPanel: React.FC<PermissionsPanelProps> = ({ 
  selectedRole,
  selectedPermissions, 
  onPermissionToggle, 
  isVisible,
  permissionsLocked = false,
  isLoadingRoleDetails = false,
  rolePermissions = [],
  isRoleEditing = false
}) => {
  const dispatch = useAppDispatch();
  const permissionsData = useSelector(getAllPermissions);

  // API call function for permissions
  const attemptGetAllPermissions = useCallback(
    async (params: any) => {
      return await dispatch(getAllPermissionsAsync(params));
    },
    [dispatch]
  );

  // Group permissions by category based on their ID prefix
  const groupPermissionsByCategory = useCallback(
    (permissions: TPermissionItem[]): PermissionGroup[] => {
      const categoryMap: Record<string, PermissionGroup> = {};

      permissions.forEach((permission) => {
        const [category] = permission.id.split('.');

        if (!categoryMap[category]) {
          // Get category info with fallback
          const categoryInfo = getCategoryInfo(category);
          categoryMap[category] = {
            id: category,
            label: categoryInfo.label,
            title: categoryInfo.title,
            description: categoryInfo.description,
            icon: categoryInfo.icon,
            color: categoryInfo.color || 'from-gray-500 to-gray-600',
            permissions: [],
          };
        }

        categoryMap[category].permissions.push(permission);
      });

      return Object.values(categoryMap).sort((a, b) =>
        a.label.localeCompare(b.label)
      );
    },
    []
  );


  // Render permissions function for InfiniteScroll
  const renderPermissions = useCallback(
    (permissions: TPermissionItem[], state: any) => {
      if (permissions.length === 0 && !state.loading) {
        return (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Δεν βρέθηκαν δικαιώματα
            </h3>
            <p className="text-gray-600 text-sm">
              Δεν υπάρχουν διαθέσιμα δικαιώματα
            </p>
          </div>
        );
      }

      const groupedPermissions = groupPermissionsByCategory(permissions);

      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.15 }}
          className="space-y-6"
        >
          {groupedPermissions.map((group, groupIndex) => (
            <PermissionGroupComponent
              key={group.id}
              group={group}
              groupIndex={groupIndex}
              selectedPermissions={selectedPermissions}
              onPermissionToggle={onPermissionToggle}
              permissionsLocked={permissionsLocked}
            />
          ))}

          {/* Load more indicator */}
          {permissions.length > 0 && state.hasMore && !state.loading && (
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="text-center py-6"
            >
              <p className="text-sm text-gray-500">
                Κύλιση για περισσότερα δικαιώματα
              </p>
            </motion.div>
          )}
        </motion.div>
      );
    },
    [selectedPermissions, onPermissionToggle, groupPermissionsByCategory, permissionsLocked]
  );

  const renderLoading = useCallback((isInitial: boolean) => {
    if (isInitial) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-3" />
            <p className="text-gray-600 text-sm">Φόρτωση δικαιωμάτων...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600 mr-2" />
        <p className="text-gray-600 text-sm">Φόρτωση περισσότερων...</p>
      </div>
    );
  }, []);

  const renderError = useCallback((error: string, retry: () => void) => {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-3">
          <Shield className="w-6 h-6 text-red-500" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Σφάλμα φόρτωσης δικαιωμάτων
        </h3>
        <p className="text-gray-500 text-center text-sm mb-4">{error}</p>
        <button
          onClick={retry}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
        >
          Δοκιμάστε ξανά
        </button>
      </div>
    );
  }, []);

  return (
    <motion.div
      initial={{ width: 0, opacity: 0 }}
      animate={{
        width: isVisible ? '100%' : 0,
        opacity: isVisible ? 1 : 0,
      }}
      transition={{
        duration: 0.7,
        ease: [0.4, 0, 0.2, 1],
        opacity: { duration: 0.5, delay: isVisible ? 0.08 : 0 },
      }}
      className="flex-1 overflow-hidden"
    >
      {isVisible && (
        <div className="h-full pl-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="h-full relative"
          >
            {/* Header */}
            <div className="mb-6">
              <h3 className="mb-2 text-xl font-bold text-gray-900">
                {permissionsLocked && selectedRole
                  ? `Δικαιώματα Ρόλου: ${selectedRole.name}`
                  : 'Προσαρμογή Δικαιωμάτων'
                }
              </h3>
              <p className="text-gray-600">
                {permissionsLocked && selectedRole
                  ? `Τα δικαιώματα καθορίζονται από τον επιλεγμένο ρόλο (${selectedPermissions.length} δικαιώματα)`
                  : 'Επιλέξτε τα δικαιώματα που θέλετε να δώσετε στον χρήστη'
                }
              </p>
            </div>

            {/* Loading role details overlay */}
            {isLoadingRoleDetails && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-xl"
              >
                <div className="flex flex-col items-center">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600 mb-2" />
                  <p className="text-gray-600 text-sm">
                    Φόρτωση δικαιωμάτων ρόλου...
                  </p>
                </div>
              </motion.div>
            )}

            {/* Permissions List - Always rendered to maintain cache */}
            <div className={`relative ${isLoadingRoleDetails ? 'pointer-events-none opacity-50' : ''}`}>
              <InfiniteScroll
                key="permissions-infinite-scroll" // Stable key to prevent remounting
                apiAction={attemptGetAllPermissions}
                itemsData={permissionsData}
                initialPageSize={100}
                params={{ sort_dir: "ASC" }}
                renderLoading={renderLoading}
                renderError={renderError}
                scroll={{
                  threshold: 300,
                  debounceMs: 200,
                }}
              >
                {renderPermissions}
              </InfiniteScroll>
            </div>

            {/* Summary */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-6 rounded-xl bg-gradient-to-r from-gray-50 to-blue-50 p-4 sticky bottom-0"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  Συνολικά επιλεγμένα δικαιώματα:
                </span>
                <span className="rounded-full bg-blue-600 px-3 py-1 text-sm font-bold text-white">
                  {selectedPermissions.length}
                </span>
              </div>
              {permissionsLocked && selectedRole && (
                <div className="mt-2 text-xs text-amber-700 flex items-center gap-1">
                  <Lock className="h-3 w-3" />
                  Δικαιώματα κλειδωμένα από ρόλο
                </div>
              )}
            </motion.div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default PermissionsPanel;